import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetProductCategories } from "@/api";
import { useQuickOrder } from "@/hooks/useQuickOrder";
import { useCommission } from "@/hooks/useCommission";
import { useSessionStore } from "@/stores/session.store";

export function useCreateOrderPage() {
  const { t } = useTranslation(["orders", "common"]);
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const quickOrder = useQuickOrder();
  const { isRestricted: isBlocked, canPayDebt } = useCommission();
  const [activeCategoryId, setActiveCategoryId] = useState<number | "all">("all");
  const [linkDismissed, setLinkDismissed] = useState(false);

  const { data: categoriesData } = useGetProductCategories(
    activeStoreId !== null ? { store_id: activeStoreId, per_page: 100 } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const categories = useMemo(
    () => [
      { id: "all" as const, name: t("orders:filters.all") },
      ...(categoriesData?.data?.data ?? []).map((category) => ({
        id: category.id,
        name: category.name,
      })),
    ],
    [categoriesData, t],
  );

  const displayProducts = useMemo(() => {
    if (activeCategoryId === "all") return quickOrder.filteredProducts;
    return quickOrder.filteredProducts.filter(
      (product) => product.category_id === activeCategoryId,
    );
  }, [activeCategoryId, quickOrder.filteredProducts]);

  const displayOrderLink = linkDismissed ? null : quickOrder.orderLink;

  const handleEditCart = useCallback(() => {
    setLinkDismissed(true);
  }, []);

  const handleReset = useCallback(() => {
    setLinkDismissed(false);
    quickOrder.resetOrder();
  }, [quickOrder]);

  const handleCreateLink = useCallback(() => {
    setLinkDismissed(false);
    quickOrder.createOrderLink();
  }, [quickOrder]);

  return {
    searchQuery: quickOrder.searchQuery,
    onSearchChange: quickOrder.setSearchQuery,
    filteredProducts: displayProducts,
    cartItems: quickOrder.cartItems,
    totalPrice: quickOrder.totalPrice,
    orderLink: displayOrderLink,
    orderError: quickOrder.orderError,
    isProductsLoading: quickOrder.isProductsLoading,
    isCreatingOrder: quickOrder.isCreatingOrder,
    categories,
    activeCategoryId,
    onCategoryChange: setActiveCategoryId,
    onAddProduct: quickOrder.addProduct,
    onRemoveProduct: quickOrder.removeProduct,
    onQuantityChange: quickOrder.updateQuantity,
    onCreateOrderLink: handleCreateLink,
    onEditCart: handleEditCart,
    onReset: handleReset,
    isBlocked,
    canPayDebt,
  };
}
